import { filter } from '@atlaskit/adf-utils/filter';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

/**
 * Below RegExp patterns taken from:
 * `jira-object-provider/src/patterns/index.ts`.
 * ---------------------------------------------
 * Note: This is originally intended to detect when a user pastes a jira link into the editor.
 * We don't need to be as specific as jira-object-provider, so we've omitted several patterns.
 * In many cases, it is suficient to just check the pasted URL using: pastedItem.indexOf('/jira/') >= 0
 */

// Returns the issue key if the URL is a valid Jira issue URL. It may also include a trailing
// hash fragment. A matching URL must look like: <host><baseurl>/browse/<issuekey><#hash?>.
// e.g. https://my-jira-instance.atlassian.net/browse/FOO-123#xyz
const issueKeyPattern = '[a-zA-Z0-9]+-\\d+';
const jiraIssueContentIdParseStr = `\\/browse\\/(?<resourceId>${issueKeyPattern})#?.*?\\/?$`;
const jiraIssueContentIdParse = new RegExp(jiraIssueContentIdParseStr);

// For Jira Service Desk work portals.
// Examples:
// * https://jdog.jira-dev.com/servicedesk/customer/portal/223/NICK-7?created=true
// * https://jdog.jira-dev.com/servicedesk/projects/NICK/queues/custom/1698/NICK-7
// * https://jdog.jira-dev.com/jira/servicedesk/projects/NICK/queues/custom/1698/NICK-7
// * https://jdog.jira-dev.com/jira/servicedesk/projects/NICK/queues/issue/NICK-7
// * https://jdog.jira-dev.com/jira/servicedesk/projects/WOOH/section/incidents/custom/272/WOOH-15
// * https://spartans.jira-dev.com/helpcenter/2/portal/23/BIREN-30?created=true
// * https://spartans.jira-dev.com/helpcenter/slug/portal/23/BIREN-30
const jiraPortalIdStr = `(\\/jira)?\\/servicedesk\\/customer\\/portal\\/\\d+\\/${issueKeyPattern}\\/?.*$`;
const jiraPortalIdParse = new RegExp(jiraPortalIdStr);
const jiraPortalQueueIdStr = `(\\/jira)?\\/servicedesk\\/projects\\/[\\w]*\\/queues\\/(custom|issue)\\/(\\d+\\/)?${issueKeyPattern}\\/?.*$`;
const jiraPortalQueueIdParse = new RegExp(jiraPortalQueueIdStr);
const jiraPortalSectionIdStr = `(\\/jira)?\\/servicedesk\\/projects\\/[\\w]*\\/section\\/(incidents|changes|service-requests|problems)\\/custom\\/\\d+\\/${issueKeyPattern}\\/?.*$`;
const jiraPortalSectionIdParse = new RegExp(jiraPortalSectionIdStr);
const jiraAdvancedHelpCenterPortalIdStr = `(\\/jira)?\\/helpcenter\\/[\\w-]+\\/portal\\/\\d+\\/${issueKeyPattern}\\/?.*$`;
const jiraAdvancedHelpCenterPortalIdParse = new RegExp(jiraAdvancedHelpCenterPortalIdStr);

// https://softwareteams.atlassian.net/projects/SAI/versions/12505/tab/release-report-all-issues
// https://hello.atlassian.net/projects/BUILDENG/versions/44334/tab/release-report-all-issues
const jiraVersionIdPattern = `\\/projects\\/[^\\/]+?\\/versions\\/\\d+\\/?`;
const jiraVersionParse = new RegExp(jiraVersionIdPattern);

// https://abocan-test-1.jira-dev.com/browse/CHEES
const jiraProjectIdPattern = `\\/browse\\/[^#\\/\?]+(\\#[^\\/]*?)?(\\?[^\\/]*?)?$`;
const jiraProjectIdParse = new RegExp(jiraProjectIdPattern);

// https://jdog.jira-dev.com/issues/?jql=project%20%3D%20%22ADIL%22%20ORDER%20BY%20created%20DESC
const jiraIssueJqlPattern = `\\/issues\\/.*?(\\&|\\?)(?!.*filter=)jql=[^&]*`;
const jiraIssueJqlParse = new RegExp(jiraIssueJqlPattern);

const jiraURLRegExps = [
	jiraIssueContentIdParse,
	jiraPortalIdParse,
	jiraPortalQueueIdParse,
	jiraPortalSectionIdParse,
	jiraAdvancedHelpCenterPortalIdParse,
	jiraVersionParse,
	jiraProjectIdParse,
	jiraIssueJqlParse,
];

const isJiraExtension = (node: ADFEntity): boolean => {
	return node.type === 'extension' && node.attrs?.extensionKey === 'jira';
};

const isJiraInlineCard = (node: ADFEntity): boolean => {
	if (node.type === 'inlineCard') {
		// according to ADF API, either url or data.url will be present but not both
		const url = node?.attrs?.url || node?.attrs?.data?.url;
		return isJiraURL(url);
	}
	return false;
};

const isJiraLink = (node: ADFEntity): boolean => {
	if (!node.marks) {
		return false;
	}
	const linkMark = node.marks.filter((mark) => mark.type === 'link')[0];
	return isJiraURL(linkMark?.attrs?.href);
};

export const doesADFContainJiraNodes = (editorContent: ADFEntity): boolean => {
	const jiraNodes = filter(
		editorContent,
		(node) => isJiraExtension(node) || isJiraInlineCard(node) || isJiraLink(node),
	);
	return jiraNodes.length > 0;
};

export const isJiraURL = (url?: string): boolean => {
	if (!url) {
		return false;
	}

	const isAtlassianURL = url.match(/atlassian\.net|jira\.com/);
	const doesUrlContainJira = isAtlassianURL && url.indexOf('/jira/') >= 0;
	const doesUrlHaveJiraIssue = Boolean(
		isAtlassianURL && url.match(/\/browse\/[a-zA-Z][a-zA-Z0-9]{0,9}-[0-9]+/),
	);
	if (doesUrlContainJira || doesUrlHaveJiraIssue) {
		return true;
	}

	for (const regExp of jiraURLRegExps) {
		if (regExp.test(url)) {
			return true;
		}
	}

	return false;
};
